/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTemplateCatalog,
  getTemplateDetail,
  uploadTemplate,
  updateTemplateMetadata,
  updateJrxml,
  updateLetterhead,
  removeLetterhead,
  deleteTemplate,
  regeneratePreview,
} from '@/services/superadmin/template-catalog.service';
import {
  TemplateCatalogItem,
  TemplateCatalogDetail,
  UpdateTemplateMetadataRequest,
} from '@/types/superadmin.types';
import { ApiError } from '@/services/api/types';

export const templateCatalogKeys = {
  all: ['templateCatalog'] as const,
  list: () => [...templateCatalogKeys.all, 'list'] as const,
  detail: (id: string) => [...templateCatalogKeys.all, 'detail', id] as const,
};

export function useTemplateCatalog() {
  return useQuery<TemplateCatalogItem[], ApiError>({
    queryKey: templateCatalogKeys.list(),
    queryFn: async () => {
      const res = await getTemplateCatalog();
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useTemplateDetail(id: string) {
  return useQuery<TemplateCatalogDetail, ApiError>({
    queryKey: templateCatalogKeys.detail(id),
    queryFn: async () => {
      const res = await getTemplateDetail(id);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useUploadTemplate() {
  const queryClient = useQueryClient();
  return useMutation<TemplateCatalogItem, ApiError, FormData>({
    mutationFn: async (data) => {
      const res = await uploadTemplate(data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.list() });
    },
  });
}

export function useUpdateTemplateMetadata() {
  const queryClient = useQueryClient();
  return useMutation<TemplateCatalogItem, ApiError, { id: string; data: UpdateTemplateMetadataRequest }>({
    mutationFn: async ({ id, data }) => {
      const res = await updateTemplateMetadata(id, data);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.list() });
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.detail(variables.id) });
    },
  });
}

export function useUpdateJrxml() {
  const queryClient = useQueryClient();
  return useMutation<TemplateCatalogItem, ApiError, { id: string; data: FormData }>({
    mutationFn: async ({ id, data }) => {
      const res = await updateJrxml(id, data);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.list() });
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.detail(variables.id) });
    },
  });
}

export function useUpdateLetterhead() {
  const queryClient = useQueryClient();
  return useMutation<TemplateCatalogItem, ApiError, { id: string; data: FormData }>({
    mutationFn: async ({ id, data }) => {
      const res = await updateLetterhead(id, data);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.list() });
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.detail(variables.id) });
    },
  });
}

export function useRemoveLetterhead() {
  const queryClient = useQueryClient();
  return useMutation<TemplateCatalogItem, ApiError, string>({
    mutationFn: async (id) => {
      const res = await removeLetterhead(id);
      return res.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.list() });
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.detail(id) });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await deleteTemplate(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.list() });
    },
  });
}

export function useRegeneratePreview() {
  const queryClient = useQueryClient();
  return useMutation<TemplateCatalogItem, ApiError, string>({
    mutationFn: async (id) => {
      const res = await regeneratePreview(id);
      return res.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.list() });
      queryClient.invalidateQueries({ queryKey: templateCatalogKeys.detail(id) });
    },
  });
}
